export type SeatKind = 'standard' | 'wheelchair' | 'held'

export type Seat = {
    id: string
    ring: string
    number: number
    /** Angle clockwise from the top of the plan, in radians. */
    angle: number
    /** Distance from the centre, 0 to 1. */
    radius: number
    kind: SeatKind
    best: boolean
}

const RINGS = [
    { ring: 'A', radius: 0.32, count: 16 },
    { ring: 'B', radius: 0.44, count: 22 },
    { ring: 'C', radius: 0.56, count: 28 },
    { ring: 'D', radius: 0.68, count: 34 },
    { ring: 'E', radius: 0.8, count: 40 },
    { ring: 'F', radius: 0.92, count: 46 }
]

/** Three radial aisles; the entrance aisle at the bottom is the widest. */
const AISLES = [
    { angle: Math.PI / 3, width: 0.05 },
    { angle: Math.PI, width: 0.085 },
    { angle: (5 * Math.PI) / 3, width: 0.05 }
]

const angularDistance = (a: number, b: number) => {
    const delta = Math.abs(a - b) % (Math.PI * 2)

    return delta > Math.PI ? Math.PI * 2 - delta : delta
}

const buildSeats = (): Seat[] => {
    const seats: Seat[] = []

    for (const { ring, radius, count } of RINGS) {
        const angles: number[] = []

        for (let k = 0; k < count; k++) {
            const angle = ((k + 0.5) / count) * Math.PI * 2
            const inAisle = AISLES.some(aisle => angularDistance(angle, aisle.angle) < aisle.width / radius)

            if (!inAisle) angles.push(angle)
        }

        // Number seats clockwise from the entrance, so seat 1 is just left of the door.
        const ordered = angles
            .map(angle => ({ angle, key: (angle - Math.PI + Math.PI * 2) % (Math.PI * 2) }))
            .sort((a, b) => a.key - b.key)

        ordered.forEach(({ angle }, index) => {
            seats.push({
                id: `${ring}${index + 1}`,
                ring,
                number: index + 1,
                angle,
                radius,
                kind: 'standard',
                best: ring === 'C' || ring === 'D'
            })
        })
    }

    const ringA = seats.filter(seat => seat.ring === 'A')
    const presenter = [...ringA].sort((a, b) => angularDistance(a.angle, 0) - angularDistance(b.angle, 0)).slice(0, 2)

    presenter.forEach(seat => {
        seat.kind = 'held'
    })

    const ringF = seats.filter(seat => seat.ring === 'F')
    const nearDoor = [...ringF].sort((a, b) => angularDistance(a.angle, Math.PI) - angularDistance(b.angle, Math.PI)).slice(0, 4)

    nearDoor.forEach(seat => {
        seat.kind = 'wheelchair'
    })

    return seats
}

export const SEATS: Seat[] = buildSeats()

export const SEAT_IDS = new Set(SEATS.map(seat => seat.id))

export const SELLABLE_SEATS = SEATS.filter(seat => seat.kind !== 'held').length

export const HELD_SEATS = SEATS.filter(seat => seat.kind === 'held').map(seat => seat.id)

export const seatById = (id: string): Seat | undefined => SEATS.find(seat => seat.id === id)

const ringOrder = 'ABCDEF'

/** Picks the best free seats together: middle rings first, then the closest neighbours. */
export const bestAvailable = (taken: Set<string>, count: number): string[] => {
    if (count <= 0) return []

    const free = SEATS.filter(seat => seat.kind === 'standard' && !taken.has(seat.id))
    const preference = ['C', 'D', 'B', 'E', 'A', 'F']

    for (const ring of preference) {
        const row = free.filter(seat => seat.ring === ring).sort((a, b) => a.number - b.number)

        for (let start = 0; start + count <= row.length; start++) {
            const run = row.slice(start, start + count)
            const contiguous = run.every((seat, index) => index === 0 || seat.number === run[index - 1].number + 1)

            if (contiguous) return run.map(seat => seat.id)
        }
    }

    return free
        .sort((a, b) => Math.abs(ringOrder.indexOf(a.ring) - 2.5) - Math.abs(ringOrder.indexOf(b.ring) - 2.5))
        .slice(0, count)
        .map(seat => seat.id)
}

export const describeSeats = (ids: string[]): string => [...ids]
    .sort((a, b) => a.localeCompare(b, 'en', { numeric: true }))
    .join(', ')
